import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getLscLiveReadiness } from '../../api';
import {
  COMMAND_CENTER_LATEST_ACTIVITY_INTRO_COPY,
  COMMAND_CENTER_LATEST_ACTIVITY_TITLE,
  COMMAND_CENTER_LIVE_DETECTION_TITLE,
} from '../../commandCenterCopy';
import { lscPath } from '../../routing';
import type { LscCommissionerDigest } from '../../types';
import {
  liveReadinessStatusHeading,
  resolveLiveReadinessDisplayState,
} from '../liveReadinessDisplay';
import { formatDigestSampleStatus } from '../CommissionerDigestCard';

export interface CommandCenterLatestActivityCardProps {
  leagueId: string;
  digest: LscCommissionerDigest | null;
  loading?: boolean;
}

function formatTimestamp(value?: string | null): string {
  if (!value) return 'Not available yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

const CommandCenterLatestActivityCard: React.FC<CommandCenterLatestActivityCardProps> = ({
  leagueId,
  digest,
  loading = false,
}) => {
  const [liveHeading, setLiveHeading] = useState<string>('Loading live detection status…');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const readiness = await getLscLiveReadiness(leagueId);
        if (cancelled) return;
        const displayState = resolveLiveReadinessDisplayState(readiness);
        setLiveHeading(liveReadinessStatusHeading(displayState));
      } catch {
        if (!cancelled) {
          setLiveHeading('Live detection status unavailable');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const latestCandidate =
    digest?.samples?.pending_review?.[0] ??
    digest?.samples?.ready_to_publish?.[0] ??
    digest?.samples?.new_synced?.[0] ??
    null;
  const latestArchive = digest?.samples?.recently_published?.[0] ?? null;

  return (
    <div
      className="rounded-xl border border-secondary-700 bg-secondary-900/40 px-4 py-4 space-y-3"
      data-testid="command-center-latest-activity-card"
    >
      <div>
        <p className="text-xs uppercase tracking-wide text-secondary-500">{COMMAND_CENTER_LATEST_ACTIVITY_TITLE}</p>
        <p className="text-sm text-secondary-400 mt-1">{COMMAND_CENTER_LATEST_ACTIVITY_INTRO_COPY}</p>
      </div>

      <ul className="space-y-3 text-sm">
        <li data-testid="command-center-latest-live-status">
          <p className="text-secondary-500 text-xs uppercase tracking-wide">{COMMAND_CENTER_LIVE_DETECTION_TITLE}</p>
          <p className="text-secondary-100 mt-0.5">{liveHeading}</p>
          <Link
            to={`${lscPath(`/leagues/${leagueId}/command-center`)}#command-center-live-detection`}
            className="text-xs text-primary-300 hover:text-primary-200 mt-1 inline-flex"
            data-testid="command-center-live-detection-link"
          >
            View live detection details
          </Link>
        </li>

        <li data-testid="command-center-latest-vod-status">
          <p className="text-secondary-500 text-xs uppercase tracking-wide">Latest replay review</p>
          {loading ? (
            <p className="text-secondary-400 mt-0.5">Loading replay status…</p>
          ) : latestCandidate ? (
            <>
              <p className="text-secondary-100 mt-0.5">{latestCandidate.title ?? 'Untitled replay'}</p>
              <p className="text-xs text-secondary-500 mt-0.5">
                {formatDigestSampleStatus(latestCandidate)} · {formatTimestamp(latestCandidate.updated_at)}
              </p>
            </>
          ) : (
            <p className="text-secondary-400 mt-0.5">No recent replay review activity yet.</p>
          )}
        </li>

        <li data-testid="command-center-latest-archive-status">
          <p className="text-secondary-500 text-xs uppercase tracking-wide">Latest archive activity</p>
          {loading ? (
            <p className="text-secondary-400 mt-0.5">Loading archive status…</p>
          ) : latestArchive ? (
            <>
              <p className="text-secondary-100 mt-0.5">{latestArchive.title ?? 'Published archive item'}</p>
              <p className="text-xs text-secondary-500 mt-0.5">
                Published {formatTimestamp(latestArchive.published_at)}
              </p>
            </>
          ) : (
            <p className="text-secondary-400 mt-0.5">No recent published archive activity yet.</p>
          )}
        </li>
      </ul>

      <Link
        to={lscPath(`/leagues/${leagueId}/archive`)}
        className="text-sm font-medium text-primary-300 hover:text-primary-200"
        data-testid="command-center-archive-link"
      >
        Open archive
      </Link>
    </div>
  );
};

export default CommandCenterLatestActivityCard;
